import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { superAdminAPI } from "../../services/api";
import { Card, Button, Spinner } from "../UI";
import PasswordInput from "./PasswordInput";
import {
  HiOutlineMail,
  HiOutlineCheckCircle,
  HiOutlineExclamation,
  HiOutlinePaperAirplane,
} from "react-icons/hi";

/**
 * SuperAdmin → Settings → Email Provider
 *
 * Configures SMTP, Brevo and Zepto credentials (each independent), then picks
 * which one is the primary sender and which (if any) is the fallback used
 * when the primary fails. Saved settings apply immediately (no restart).
 */

const inputClass =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-50";

const StatusLine = ({ ok, children }) => (
  <p className={`flex items-start gap-1.5 text-xs ${ok ? "text-green-700" : "text-amber-700"}`}>
    {ok ? (
      <HiOutlineCheckCircle className="w-4 h-4 flex-shrink-0" />
    ) : (
      <HiOutlineExclamation className="w-4 h-4 flex-shrink-0" />
    )}
    <span>{children}</span>
  </p>
);

const RoleBadge = ({ provider, primaryProvider, fallbackProvider }) => {
  if (provider === primaryProvider) {
    return <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">Primary</span>;
  }
  if (provider === fallbackProvider) {
    return <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700">Fallback</span>;
  }
  return null;
};

const EmailProviderSettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(null);
  const [newSmtpPass, setNewSmtpPass] = useState("");
  const [newBrevoKey, setNewBrevoKey] = useState("");
  const [newZeptoKey, setNewZeptoKey] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["sa-email-settings"],
    queryFn: async () => (await superAdminAPI.getEmailSettings()).data?.data,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      primaryProvider: data.primaryProvider || "smtp",
      fallbackProvider: data.fallbackProvider || "",
      smtp: {
        host: data.smtp?.host || "",
        port: data.smtp?.port || 587,
        secure: !!data.smtp?.secure,
        user: data.smtp?.user || "",
        fromEmail: data.smtp?.fromEmail || "",
        fromName: data.smtp?.fromName || "",
      },
      brevo: {
        fromEmail: data.brevo?.fromEmail || "",
        fromName: data.brevo?.fromName || "",
      },
      zepto: {
        apiUrl: data.zepto?.apiUrl || "",
        fromEmail: data.zepto?.fromEmail || "",
        fromName: data.zepto?.fromName || "",
      },
    });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (body) => superAdminAPI.updateEmailSettings(body),
    onSuccess: (response) => {
      setNewSmtpPass("");
      setNewBrevoKey("");
      setNewZeptoKey("");
      queryClient.setQueryData(["sa-email-settings"], response.data?.data);
      toast.success("Email settings saved — used for the next email");
    },
    onError: (err) => toast.error(err.response?.data?.message || "Failed to save email settings"),
  });

  const runTest = async (provider) => {
    const to = testTo.trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Enter a valid recipient email above first");
      return;
    }
    setTesting(provider);
    try {
      const response = await superAdminAPI.testEmailSettings({ provider, to });
      toast.success(response.data?.message || `Test email sent via ${provider}`);
    } catch (err) {
      toast.error(err.response?.data?.message || `${provider} test failed`, { duration: 6000 });
    } finally {
      setTesting(null);
    }
  };

  if (isLoading || (!form && !error)) {
    return (
      <Card padding="p-6" className="flex justify-center">
        <Spinner />
      </Card>
    );
  }
  if (error) {
    return (
      <Card padding="p-6">
        <StatusLine ok={false}>
          {error.response?.data?.message || "Failed to load email settings"}
        </StatusLine>
      </Card>
    );
  }

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const setSmtp = (patch) => setForm((prev) => ({ ...prev, smtp: { ...prev.smtp, ...patch } }));
  const setBrevo = (patch) => setForm((prev) => ({ ...prev, brevo: { ...prev.brevo, ...patch } }));
  const setZepto = (patch) => setForm((prev) => ({ ...prev, zepto: { ...prev.zepto, ...patch } }));

  const isDirty =
    newSmtpPass.trim() !== "" ||
    newBrevoKey.trim() !== "" ||
    newZeptoKey.trim() !== "" ||
    form.primaryProvider !== (data.primaryProvider || "smtp") ||
    form.fallbackProvider !== (data.fallbackProvider || "") ||
    form.smtp.host !== (data.smtp?.host || "") ||
    form.smtp.port !== (data.smtp?.port || 587) ||
    form.smtp.secure !== !!data.smtp?.secure ||
    form.smtp.user !== (data.smtp?.user || "") ||
    form.smtp.fromEmail !== (data.smtp?.fromEmail || "") ||
    form.smtp.fromName !== (data.smtp?.fromName || "") ||
    form.brevo.fromEmail !== (data.brevo?.fromEmail || "") ||
    form.brevo.fromName !== (data.brevo?.fromName || "") ||
    form.zepto.apiUrl !== (data.zepto?.apiUrl || "") ||
    form.zepto.fromEmail !== (data.zepto?.fromEmail || "") ||
    form.zepto.fromName !== (data.zepto?.fromName || "");

  const save = () => {
    const body = {
      primaryProvider: form.primaryProvider,
      fallbackProvider: form.fallbackProvider,
      smtp: { ...form.smtp },
      brevo: { ...form.brevo },
      zepto: { ...form.zepto },
    };
    if (newSmtpPass.trim()) body.smtp.pass = newSmtpPass.trim();
    if (newBrevoKey.trim()) body.brevo.apiKey = newBrevoKey.trim();
    if (newZeptoKey.trim()) body.zepto.apiKey = newZeptoKey.trim();
    saveMutation.mutate(body);
  };

  const clearSecret = (provider) => {
    if (!window.confirm(`Remove the saved ${provider.toUpperCase()} credential?`)) return;
    const key = provider === "smtp" ? "smtp" : provider;
    const clearField = provider === "smtp" ? "clearPass" : "clearApiKey";
    saveMutation.mutate({ [key]: { [clearField]: true } });
  };

  return (
    <Card padding="p-4 sm:p-6" className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
          <HiOutlineMail className="w-5 h-5 text-purple-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-gray-900">Email Provider</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure SMTP, Brevo and Zepto, then pick which one sends email and which one
            takes over if it fails. Changes apply to the next email — no server restart needed.
          </p>
        </div>
      </div>

      {/* Primary / Fallback role selectors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-100 pt-5">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="email-primary-provider">
            Primary provider
          </label>
          <select
            id="email-primary-provider"
            className={inputClass}
            value={form.primaryProvider}
            onChange={(e) => set({ primaryProvider: e.target.value })}
          >
            <option value="smtp">SMTP</option>
            <option value="brevo">Brevo</option>
            <option value="zepto">Zepto</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="email-fallback-provider">
            Fallback provider
          </label>
          <select
            id="email-fallback-provider"
            className={inputClass}
            value={form.fallbackProvider}
            onChange={(e) => set({ fallbackProvider: e.target.value })}
          >
            <option value="">None</option>
            <option value="smtp">SMTP</option>
            <option value="brevo">Brevo</option>
            <option value="zepto">Zepto</option>
          </select>
        </div>
      </div>

      {/* Shared test recipient */}
      <div className="border-t border-gray-100 pt-5">
        <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="email-test-to">
          Test recipient email
        </label>
        <input
          id="email-test-to"
          type="email"
          className={inputClass}
          value={testTo}
          placeholder="you@example.com"
          onChange={(e) => setTestTo(e.target.value)}
        />
      </div>

      {/* SMTP */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          SMTP
          <RoleBadge provider="smtp" primaryProvider={form.primaryProvider} fallbackProvider={form.fallbackProvider} />
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="smtp-host">Host</label>
            <input id="smtp-host" className={inputClass} value={form.smtp.host} placeholder="smtp.hostinger.com" onChange={(e) => setSmtp({ host: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="smtp-port">Port</label>
            <input id="smtp-port" type="number" className={inputClass} value={form.smtp.port} onChange={(e) => setSmtp({ port: parseInt(e.target.value, 10) || "" })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="smtp-user">Username</label>
            <input id="smtp-user" className={inputClass} value={form.smtp.user} onChange={(e) => setSmtp({ user: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="smtp-pass">Password</label>
            <PasswordInput
              id="smtp-pass"
              className={inputClass}
              value={newSmtpPass}
              autoComplete="off"
              placeholder={data.smtp?.passSet ? `Saved: ${data.smtp.passMasked} — enter to replace` : "SMTP password"}
              onChange={(e) => setNewSmtpPass(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="smtp-from-email">From email</label>
            <input id="smtp-from-email" className={inputClass} value={form.smtp.fromEmail} onChange={(e) => setSmtp({ fromEmail: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="smtp-from-name">From name</label>
            <input id="smtp-from-name" className={inputClass} value={form.smtp.fromName} placeholder="TeleValley" onChange={(e) => setSmtp({ fromName: e.target.value })} />
          </div>
          <div className="flex items-center gap-2 lg:col-span-2">
            <input
              type="checkbox"
              id="smtp-secure"
              checked={form.smtp.secure}
              onChange={(e) => setSmtp({ secure: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <label htmlFor="smtp-secure" className="text-sm text-gray-700">Use TLS/SSL (secure)</label>
          </div>
        </div>
        {data.smtp?.passSet && (
          <StatusLine ok>
            Password saved ({data.smtp.passMasked}), stored encrypted.{" "}
            <button type="button" onClick={() => clearSecret("smtp")} className="underline text-red-600">Remove</button>
          </StatusLine>
        )}
        <Button
          variant="outline"
          size="sm"
          icon={<HiOutlinePaperAirplane className="w-4 h-4" />}
          onClick={() => runTest("smtp")}
          loading={testing === "smtp"}
          disabled={!!testing || isDirty}
          title={isDirty ? "Save first, then test" : undefined}
        >
          Send test email via SMTP
        </Button>
      </div>

      {/* Brevo */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          Brevo
          <RoleBadge provider="brevo" primaryProvider={form.primaryProvider} fallbackProvider={form.fallbackProvider} />
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="brevo-key">API key</label>
            <PasswordInput
              id="brevo-key"
              className={inputClass}
              value={newBrevoKey}
              autoComplete="off"
              placeholder={data.brevo?.apiKeySet ? `Saved: ${data.brevo.apiKeyMasked} — enter to replace` : "xkeysib-…"}
              onChange={(e) => setNewBrevoKey(e.target.value)}
            />
          </div>
          <div />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="brevo-from-email">From email</label>
            <input id="brevo-from-email" className={inputClass} value={form.brevo.fromEmail} onChange={(e) => setBrevo({ fromEmail: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="brevo-from-name">From name</label>
            <input id="brevo-from-name" className={inputClass} value={form.brevo.fromName} placeholder="TeleValley" onChange={(e) => setBrevo({ fromName: e.target.value })} />
          </div>
        </div>
        {data.brevo?.apiKeySet && (
          <StatusLine ok>
            Key saved ({data.brevo.apiKeyMasked}), stored encrypted.{" "}
            <button type="button" onClick={() => clearSecret("brevo")} className="underline text-red-600">Remove</button>
          </StatusLine>
        )}
        <Button
          variant="outline"
          size="sm"
          icon={<HiOutlinePaperAirplane className="w-4 h-4" />}
          onClick={() => runTest("brevo")}
          loading={testing === "brevo"}
          disabled={!!testing || (!data.brevo?.apiKeySet && !newBrevoKey) || isDirty}
          title={isDirty ? "Save first, then test" : undefined}
        >
          Send test email via Brevo
        </Button>
      </div>

      {/* Zepto */}
      <div className="space-y-3 border-t border-gray-100 pt-5">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          Zepto
          <RoleBadge provider="zepto" primaryProvider={form.primaryProvider} fallbackProvider={form.fallbackProvider} />
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="zepto-key">Send Mail Token</label>
            <PasswordInput
              id="zepto-key"
              className={inputClass}
              value={newZeptoKey}
              autoComplete="off"
              placeholder={data.zepto?.apiKeySet ? `Saved: ${data.zepto.apiKeyMasked} — enter to replace` : "Token only — without the \"Zoho-enczapikey \" prefix"}
              onChange={(e) => setNewZeptoKey(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="zepto-url">API URL</label>
            <input
              id="zepto-url"
              className={inputClass}
              value={form.zepto.apiUrl}
              placeholder="https://api.zeptomail.in/v1.1/email"
              onChange={(e) => setZepto({ apiUrl: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="zepto-from-email">From email</label>
            <input id="zepto-from-email" className={inputClass} value={form.zepto.fromEmail} onChange={(e) => setZepto({ fromEmail: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="zepto-from-name">From name</label>
            <input id="zepto-from-name" className={inputClass} value={form.zepto.fromName} placeholder="TeleValley" onChange={(e) => setZepto({ fromName: e.target.value })} />
          </div>
        </div>
        {data.zepto?.apiKeySet && (
          <StatusLine ok>
            Token saved ({data.zepto.apiKeyMasked}), stored encrypted.{" "}
            <button type="button" onClick={() => clearSecret("zepto")} className="underline text-red-600">Remove</button>
          </StatusLine>
        )}
        <Button
          variant="outline"
          size="sm"
          icon={<HiOutlinePaperAirplane className="w-4 h-4" />}
          onClick={() => runTest("zepto")}
          loading={testing === "zepto"}
          disabled={!!testing || (!data.zepto?.apiKeySet && !newZeptoKey) || isDirty}
          title={isDirty ? "Save first, then test" : undefined}
        >
          Send test email via Zepto
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-gray-100 pt-5">
        <p className="text-xs text-gray-400">
          {data.updatedAt
            ? `Last changed ${new Date(data.updatedAt).toLocaleString()}${data.updatedBy ? ` by ${data.updatedBy}` : ""}`
            : "Using server .env defaults"}
        </p>
        <Button onClick={save} loading={saveMutation.isPending} disabled={!isDirty || saveMutation.isPending}>
          Save email settings
        </Button>
      </div>
    </Card>
  );
};

export default EmailProviderSettings;
